import React, { useMemo, useState } from 'react';
import { CheckIcon, PlusIcon, XIcon, SearchIcon, ShoppingBagIcon, CameraOffIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { inventoryRooms, labelForKey, specialItems, suggestedInventory } from '../../data/inventory';
import { imageForKey } from '../../data/itemImages';
import type { StepErrors } from '../../utils/validation';
import { QuantityStepper } from './QuantityStepper';
import { StepHeading } from './StepHeading';
import { TextField } from './TextField';
import { SuggestedItems } from './SuggestedItems';
import { InventoryItemCard } from './InventoryItemCard';
import { HelpAnswers } from './HelpAnswers';
const copy = {
  en: {
    all: 'All rooms',
    search: 'Search items (bed, TV, dresser…)',
    noResults: 'No items match. Use “I don’t see my item” below.',
    cart: 'Your items',
    cartEmpty: 'Nothing added yet. Every item starts at zero. Tap a card to add it.',
    note: 'Note or measurements (optional)',
    hint: 'Reference images. Pick a variant, tap the card to add one, then adjust the quantity.',
    review: 'Our team reviews handling for this piece.',
    missing: 'I don’t see my item',
    photos: 'Photo upload turns on once secure storage is connected. Describe the item in the note for now.',
    total: 'items'
  },
  es: {
    all: 'Todas',
    search: 'Buscar artículos (cama, TV, cómoda…)',
    noResults: 'Ningún artículo coincide. Use “No veo mi artículo” abajo.',
    cart: 'Sus artículos',
    cartEmpty: 'Aún no agrega nada. Todo empieza en cero: toque una tarjeta para agregarla.',
    note: 'Nota o medidas (opcional)',
    hint: 'Imágenes de referencia. Elija la variante, toque la tarjeta para agregar una y ajuste la cantidad.',
    review: 'Nuestro equipo revisa el manejo de esta pieza.',
    missing: 'No veo mi artículo',
    photos: 'La carga de fotos se activa cuando el almacenamiento seguro esté conectado. Por ahora, describa el artículo en la nota.',
    total: 'artículos'
  }
};
function CheckRow({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return <label className="flex min-h-[44px] cursor-pointer items-center gap-3" data-id="element-763" data-mp="1987v9fei98c0">
      <input type="checkbox" className="peer sr-only" checked={checked} onChange={e => onChange(e.target.checked)} data-id="element-764" data-mp="vc46i1mehs6x" />
      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-magenta ${checked ? 'border-ink bg-ink text-lime' : 'border-ink-300 bg-white'}`} aria-hidden="true" data-id="element-765" data-mp="10bqiy8uen4eo">
        {checked && <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-766" data-mp="1j8ib77rxctuv" />}
      </span>
      <span className="text-[15px] text-ink" data-id="element-767" data-mp="1j6np3q4x2vp7">{label}</span>
    </label>;
}
export function StepInventory({
  errors
}: {
  errors: StepErrors;
}) {
  const {
    t,
    lang,
    data,
    update
  } = useFunnel();
  const c = copy[lang];
  const [room, setRoom] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [newItem, setNewItem] = useState('');
  const suggested = data.bedrooms && data.homeType !== 'office' ? suggestedInventory[data.bedrooms] : {};
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rooms = room === 'all' ? inventoryRooms : inventoryRooms.filter(r => r.id === room);
    const items = rooms.flatMap(r => r.items);
    if (!q) return items;
    return items.filter(i => `${i.label.en} ${i.label.es} ${i.keywords ?? ''} ${(i.variants ?? []).map(v => `${v.label.en} ${v.label.es}`).join(' ')}`.toLowerCase().includes(q));
  }, [room, query]);
  const selected = Object.entries(data.inventory).filter(([, q]) => q > 0);
  const totalCount = selected.reduce((s, [, q]) => s + q, 0) + data.customItems.reduce((s, x) => s + x.qty, 0);
  const setQty = (key: string, n: number) => {
    const next = {
      ...data.inventory
    };
    if (n > 0) next[key] = n;else delete next[key];
    update({
      inventory: next
    });
  };
  const removeKeys = (keys: string[]) => {
    const next = {
      ...data.inventory
    };
    keys.forEach(k => delete next[k]);
    update({
      inventory: next
    });
  };
  const toggleSpecial = (id: string) => update({
    specialItems: data.specialItems.includes(id) ? data.specialItems.filter(s => s !== id) : [...data.specialItems, id]
  });
  const addCustom = () => {
    const name = newItem.trim();
    if (!name) return;
    update({
      customItems: [...data.customItems, {
        id: `c${Date.now()}`,
        name: name.slice(0, 60),
        qty: 1
      }]
    });
    setNewItem('');
  };
  return <div data-id="element-768" data-mp="2kq0xcsrto8yq">
      <StepHeading title={t('invTitle')} intro={t('invIntro')} data-id="element-769" data-mp="2kqapon0cas7l" />

      <div className="relative" data-id="element-770" data-mp="2ppnirpht0vim">
        <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-400" aria-hidden="true" data-id="element-771" data-mp="2polweovc8mll" />
        <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={c.search} aria-label={c.search} className="h-12 w-full rounded-xl border-2 border-ink-200 bg-white pl-12 pr-4 text-base focus:border-ink focus:outline-none" data-id="element-772" data-mp="2tt20qlpazlyp" />
      </div>

      <div role="tablist" aria-label={t('invTitle')} className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0" data-id="element-773" data-mp="e20hsvvs5ac9">
        {[{
        id: 'all',
        label: {
          en: c.all,
          es: c.all
        }
      }, ...inventoryRooms].map(r => {
        const count = r.id === 'all' ? 0 : inventoryRooms.find(x => x.id === r.id)!.items.reduce((s, i) => s + selected.filter(([k]) => k.split(':')[0] === i.id).reduce((a, [, q]) => a + q, 0), 0);
        const sel = r.id === room;
        return <button key={r.id} role="tab" type="button" aria-selected={sel} aria-controls="inv-panel" onClick={() => setRoom(r.id)} className={`flex min-h-[44px] shrink-0 items-center gap-2 whitespace-nowrap rounded-full border-2 px-4 text-sm font-semibold transition-[background-color,border-color] duration-150 ease-out ${sel ? 'border-ink bg-ink text-white' : 'border-ink-200 bg-white text-ink hover:border-ink-400'}`} data-id="element-774" data-mp="wytfr5fw6zcx">
              {r.label[lang]}
              {count > 0 && <span className={`rounded-full px-1.5 text-xs font-bold ${sel ? 'bg-lime text-ink' : 'bg-ink-100 text-ink'}`} data-id="element-775" data-mp="1yw0r2738e14x">{count}</span>}
            </button>;
      })}
      </div>

      <div className="mt-4 grid gap-6 xl:grid-cols-[1fr_260px]" data-id="element-776" data-mp="1pvkr37lp5c4t">
        <div id="inv-panel" role="tabpanel" className="rounded-2xl bg-white p-3 ring-1 ring-ink-200 sm:p-4" data-id="element-777" data-mp="1pwvtb2zhubtt">
          {visible.length === 0 ? <p className="p-6 text-center text-sm text-ink-500" data-id="element-778" data-mp="1fwv5p4uld9jt">{c.noResults}</p> : <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-779" data-mp="36a3wgzt09xp7">
              {visible.map(item => <InventoryItemCard key={item.id} item={item} lang={lang} inventory={data.inventory} suggested={suggested} onSet={setQty} onRemove={removeKeys} labels={{
            suggested: t('suggested'),
            dec: t('decrease'),
            inc: t('increase'),
            remove: t('remove'),
            review: c.review
          }} data-id="element-780" data-mp="3abxo6il83rd8" />)}
            </div>}
          <p className="mt-3 text-xs text-ink-500" data-id="element-781" data-mp="1ibta5ll2vcsj">{c.hint}</p>
          {data.homeType !== 'office' && room !== 'all' && <div className="mt-3 border-t border-ink-100 pt-3" data-id="element-782" data-mp="1dbx5rl8gxjcg">
              <SuggestedItems roomItemIds={inventoryRooms.find(r => r.id === room)?.items.map(i => i.id)} data-id="element-783" data-mp="1rcrek4zfhmbn" />
            </div>}
        </div>

        <aside aria-label={c.cart} className="self-start rounded-2xl bg-white p-4 ring-1 ring-ink-200 xl:sticky xl:top-24" data-id="element-784" data-mp="1me6fw9ztrapb">
          <h2 className="flex items-center gap-2 font-display text-lg font-extrabold text-ink" data-id="element-785" data-mp="3t679vf6gnpcy">
            <ShoppingBagIcon className="h-5 w-5 text-lime-dark" aria-hidden="true" data-id="element-786" data-mp="3o74fqps0r3jr" />
            {c.cart}
            <span className="ml-auto rounded-full bg-ink px-2 text-sm font-bold text-lime" data-id="element-787" data-mp="337j19jv1jvtz">{totalCount}</span>
          </h2>
          {selected.length === 0 && data.customItems.length === 0 ? <p className="mt-3 text-sm text-ink-500" data-id="element-788" data-mp="3c3qkuv2ppehx">{c.cartEmpty}</p> : <ul className="mt-3 max-h-[480px] space-y-3 overflow-y-auto pr-1" data-id="element-789" data-mp="3gzyuxu21yhi8">
              {selected.map(([key, q]) => {
            const src = imageForKey(key);
            return <li key={key} className="border-b border-ink-100 pb-3 last:border-0" data-id="element-790" data-mp="2occu2tk7lqz2">
                    <div className="flex items-center gap-2" data-id="element-791" data-mp="2t9l4u9312sqa">
                      {src && <img src={src} alt="" className="h-10 w-10 shrink-0 rounded-md bg-[#F7F6F2] object-contain" data-id="element-792" data-mp="2yaish16t4ez6" />}
                      <span className="flex-1 text-sm font-semibold leading-tight text-ink" data-id="element-793" data-mp="8k5h5mz3vk4v">{labelForKey(key, lang)}</span>
                      <button type="button" aria-label={`${t('remove')}: ${labelForKey(key, lang)}`} onClick={() => setQty(key, 0)} className="flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink" data-id="element-794" data-mp="dm4laz2o6l2v">
                        <XIcon className="h-4 w-4" data-id="element-795" data-mp="1obr6htlg8149" />
                      </button>
                    </div>
                    <div className="mt-1 flex items-center gap-2" data-id="element-796" data-mp="1jdy3tajctp8q">
                      <button type="button" aria-label={t('decrease')} onClick={() => setQty(key, q - 1)} className="h-8 w-8 rounded-md border border-ink-200 font-bold hover:bg-ink-100" data-id="element-797" data-mp="1fbkxol0p3pbf">−</button>
                      <span className="w-6 text-center font-bold tabular-nums" data-id="element-798" data-mp="1aeug1t98slnw">{q}</span>
                      <button type="button" aria-label={t('increase')} onClick={() => setQty(key, q + 1)} className="h-8 w-8 rounded-md bg-ink font-bold text-white hover:bg-ink-700" data-id="element-799" data-mp="15fho4u3ikoeg">+</button>
                    </div>
                    <input value={data.itemNotes[key] ?? ''} onChange={e => update({
                itemNotes: {
                  ...data.itemNotes,
                  [key]: e.target.value.slice(0, 120)
                }
              })} placeholder={c.note} aria-label={`${c.note}: ${labelForKey(key, lang)}`} className="mt-2 h-9 w-full rounded-md border border-ink-200 px-2 text-sm focus:border-ink focus:outline-none" data-id="element-800" data-mp="2usblfxnil8gb" />
                  </li>;
          })}
              {data.customItems.map(x => <li key={x.id} className="flex items-center justify-between text-sm" data-id="element-801" data-mp="3j4hnelul9xh9">
                  <span className="font-semibold text-ink" data-id="element-802" data-mp="3e9bzjhhmm8qj">{x.name}</span>
                  <span className="text-ink-500" data-id="element-803" data-mp="3sxgimmklcm9u">×{x.qty}</span>
                </li>)}
            </ul>}
        </aside>
      </div>

      {errors.inventory && <p className="mt-3 text-sm font-medium text-magenta-dark" data-id="element-804" data-mp="30fko23kc4es6">{t(errors.inventory)}</p>}

      <section className="mt-8" data-id="element-805" data-mp="2vh9dfqm7u08w">
        <h2 className="font-display text-lg font-extrabold text-ink" data-id="element-806" data-mp="2vg83jpgxrjsr">{c.missing}</h2>
        <form className="mt-2 flex gap-2" onSubmit={e => {
        e.preventDefault();
        addCustom();
      }} data-id="element-807" data-mp="1ayp9couj8t8d">
          <input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder={t('addItemPlaceholder')} aria-label={c.missing} className="h-12 flex-1 rounded-lg border-2 border-ink-200 bg-white px-4 text-base focus:border-ink focus:outline-none" data-id="element-808" data-mp="110sxfutrtu97" />
          <button type="submit" className="flex h-12 items-center gap-1.5 whitespace-nowrap rounded-lg bg-ink px-4 font-semibold text-white hover:bg-ink-700" data-id="element-809" data-mp="1yot8ok0ou6tk">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-810" data-mp="1kr4n19ysqvng" />
            {t('addItemBtn')}
          </button>
        </form>
        <p className="mt-2 flex items-start gap-2 text-xs text-ink-500" data-id="element-811" data-mp="99v8n1uiytt0">
          <CameraOffIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-812" data-mp="9bzie0f3un17" />
          {c.photos}
        </p>
        {data.customItems.length > 0 && <ul className="mt-3 divide-y divide-ink-200 rounded-xl border border-ink-200 bg-white" data-id="element-813" data-mp="2fwkhaq1ts4k5">
            {data.customItems.map(x => <li key={x.id} className="flex items-center gap-2 pr-2" data-id="element-814" data-mp="2682k0isr8fl9">
                <div className="flex-1" data-id="element-815" data-mp="2669cl1abhrc7">
                  <QuantityStepper label={x.name} value={x.qty} onChange={n => update({
              customItems: data.customItems.map(y => y.id === x.id ? {
                ...y,
                qty: Math.max(1, n)
              } : y)
            })} decLabel={t('decrease')} incLabel={t('increase')} data-id="element-816" data-mp="21997aslaksm5" />
                </div>
                <button type="button" aria-label={`${t('remove')}: ${x.name}`} onClick={() => update({
            customItems: data.customItems.filter(y => y.id !== x.id)
          })} className="flex h-11 w-11 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink" data-id="element-817" data-mp="1nb196hcirl9e">
                  <XIcon className="h-4 w-4" data-id="element-818" data-mp="3l4n9gbo179cr" />
                </button>
              </li>)}
          </ul>}
      </section>

      <fieldset className="mt-10" data-id="element-819" data-mp="3txqkyef49c4r">
        <legend className="font-display text-xl font-extrabold text-ink" data-id="element-820" data-mp="3tyi6ewf01thb">{t('specialTitle')}</legend>
        <p className="mb-4 mt-1 text-sm text-ink-500" data-id="element-821" data-mp="2q4deapa0pim">{t('specialHelp')}</p>
        <div className="flex flex-wrap gap-2" data-id="element-822" data-mp="326srh84xdni8">
          {specialItems.map(s => {
          const on = data.specialItems.includes(s.id);
          return <button key={s.id} type="button" role="checkbox" aria-checked={on} onClick={() => toggleSpecial(s.id)} className={`flex min-h-[44px] items-center gap-2 rounded-lg border-2 px-4 text-sm font-semibold transition-[border-color,background-color] duration-150 ease-out ${on ? 'border-ink bg-ink text-white' : 'border-ink-200 bg-white text-ink hover:border-ink-400'}`} data-id="element-823" data-mp="369pgbgl26fum">
                <span className={`flex h-4 w-4 items-center justify-center rounded border ${on ? 'border-lime bg-lime text-ink' : 'border-ink-300'}`} aria-hidden="true" data-id="element-824" data-mp="1ze87vysfq3ed">
                  {on && <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-825" data-mp="28ed37akctbc6" />}
                </span>
                {s.label[lang]}
              </button>;
        })}
        </div>
      </fieldset>

      <div className="mt-8 space-y-1" data-id="element-826" data-mp="2ickhtmr67hb0">
        <CheckRow checked={data.inventoryUnsure} onChange={v => update({
        inventoryUnsure: v
      })} label={t('invUnsure')} data-id="element-827" data-mp="2ibhshj9hxmx7" />
      </div>

      <TextField className="mt-6" multiline label={t('invNotes')} value={data.inventoryNotes} onChange={v => update({
      inventoryNotes: v
    })} data-id="element-828" data-mp="2w3613ckjdt9f" />
      <HelpAnswers screen="stepInventory" className="mt-10" data-id="element-829" data-mp="361lujm71dem3" />
    </div>;
}